import { doc, getDoc, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import dayjs from 'dayjs';
import { db } from '../config/firebase';
import { CalendarConfig } from '../types/calendarConfig';

const COLLECTION_NAME = 'calendar-config';
const CONFIG_DOC_ID = 'global-settings';

const DEFAULT_CALENDAR_CONFIG: CalendarConfig = {
  weekdayShiftCapacity: 6,
  weekendShiftCapacity: 8,
  weekdayTableCapacity: 3,
  weekdayBarCapacity: 2,
  weekdaySecurityCapacity: 1,
  weekendTableCapacity: 4,
  weekendBarCapacity: 3,
  weekendSecurityCapacity: 1,
  storeLocations: ['Cửa hàng 1'],
  scheduleDeadlineDay: 6,
  scheduleDeadlineHour: 18,
  scheduleDeadlineMinute: 0,
  scheduleResetDay: 0,
  scheduleResetHour: 0,
  scheduleResetMinute: 5,
  calendarEnabled: true,
  expectedWeeklyUsers: 20,
};

const toDate = (value: unknown): Date | undefined => {
  if (!value) {
    return undefined;
  }
  if (value instanceof Date) {
    return value;
  }
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return new Date(value);
  }
  return undefined;
};

const normalizeWeekStart = (input: Date): Date => {
  const date = new Date(input);
  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diffToMonday);
  date.setHours(0, 0, 0, 0);
  return date;
};

const ensureNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const calendarConfigService = {
  getDefaultConfig(): CalendarConfig {
    return { ...DEFAULT_CALENDAR_CONFIG };
  },

  async getCalendarConfig(): Promise<CalendarConfig> {
    const ref = doc(db, COLLECTION_NAME, CONFIG_DOC_ID);
    const snapshot = await getDoc(ref);

    if (!snapshot.exists()) {
      await setDoc(ref, {
        ...DEFAULT_CALENDAR_CONFIG,
        updatedAt: new Date(),
        updatedBy: 'system',
      });
      return { ...DEFAULT_CALENDAR_CONFIG };
    }

    const data = snapshot.data();

    return {
      ...DEFAULT_CALENDAR_CONFIG,
      ...data,
      weekdayShiftCapacity: ensureNumber(data.weekdayShiftCapacity, DEFAULT_CALENDAR_CONFIG.weekdayShiftCapacity),
      weekendShiftCapacity: ensureNumber(data.weekendShiftCapacity, DEFAULT_CALENDAR_CONFIG.weekendShiftCapacity),
      weekdayTableCapacity: ensureNumber(data.weekdayTableCapacity, DEFAULT_CALENDAR_CONFIG.weekdayTableCapacity),
      weekdayBarCapacity: ensureNumber(data.weekdayBarCapacity, DEFAULT_CALENDAR_CONFIG.weekdayBarCapacity),
      weekdaySecurityCapacity: ensureNumber(
        data.weekdaySecurityCapacity,
        DEFAULT_CALENDAR_CONFIG.weekdaySecurityCapacity
      ),
      weekendTableCapacity: ensureNumber(data.weekendTableCapacity, DEFAULT_CALENDAR_CONFIG.weekendTableCapacity),
      weekendBarCapacity: ensureNumber(data.weekendBarCapacity, DEFAULT_CALENDAR_CONFIG.weekendBarCapacity),
      weekendSecurityCapacity: ensureNumber(
        data.weekendSecurityCapacity,
        DEFAULT_CALENDAR_CONFIG.weekendSecurityCapacity
      ),
      storeLocations: Array.isArray(data.storeLocations)
        ? data.storeLocations.filter((item) => typeof item === 'string')
        : DEFAULT_CALENDAR_CONFIG.storeLocations,
      scheduleDeadlineDay: ensureNumber(data.scheduleDeadlineDay, DEFAULT_CALENDAR_CONFIG.scheduleDeadlineDay),
      scheduleDeadlineHour: ensureNumber(data.scheduleDeadlineHour, DEFAULT_CALENDAR_CONFIG.scheduleDeadlineHour),
      scheduleDeadlineMinute: ensureNumber(
        data.scheduleDeadlineMinute,
        DEFAULT_CALENDAR_CONFIG.scheduleDeadlineMinute
      ),
      scheduleResetDay: ensureNumber(data.scheduleResetDay, DEFAULT_CALENDAR_CONFIG.scheduleResetDay),
      scheduleResetHour: ensureNumber(data.scheduleResetHour, DEFAULT_CALENDAR_CONFIG.scheduleResetHour),
      scheduleResetMinute: ensureNumber(data.scheduleResetMinute, DEFAULT_CALENDAR_CONFIG.scheduleResetMinute),
      calendarEnabled: Boolean(data.calendarEnabled),
      expectedWeeklyUsers: ensureNumber(data.expectedWeeklyUsers, DEFAULT_CALENDAR_CONFIG.expectedWeeklyUsers),
      updatedAt: toDate(data.updatedAt),
      updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : undefined,
    };
  },

  async updateCalendarConfig(payload: Partial<CalendarConfig>, updatedBy = 'system'): Promise<void> {
    const ref = doc(db, COLLECTION_NAME, CONFIG_DOC_ID);
    const snapshot = await getDoc(ref);

    if (!snapshot.exists()) {
      await setDoc(ref, {
        ...DEFAULT_CALENDAR_CONFIG,
        ...payload,
        updatedAt: new Date(),
        updatedBy,
      });
      return;
    }

    await updateDoc(ref, {
      ...payload,
      updatedAt: new Date(),
      updatedBy,
    });
  },

  async isPastDeadline(targetWeek: Date, referenceTime = new Date()): Promise<boolean> {
    const config = await this.getCalendarConfig();
    const weekStart = normalizeWeekStart(targetWeek);
    const deadline = new Date(weekStart);

    deadline.setDate(deadline.getDate() + config.scheduleDeadlineDay - 7);
    deadline.setHours(config.scheduleDeadlineHour, config.scheduleDeadlineMinute, 0, 0);

    return referenceTime.getTime() > deadline.getTime();
  },

  async getFormattedDeadline(targetWeek: Date): Promise<string> {
    const config = await this.getCalendarConfig();
    const weekStart = normalizeWeekStart(targetWeek);
    const deadline = new Date(weekStart);

    deadline.setDate(deadline.getDate() + config.scheduleDeadlineDay - 7);
    deadline.setHours(config.scheduleDeadlineHour, config.scheduleDeadlineMinute, 0, 0);

    return dayjs(deadline).format('HH:mm DD/MM/YYYY');
  },

  getPositionCapacitiesForDay(dayIndex: number, config: CalendarConfig): {
    table: number;
    bar: number;
    security: number;
  } {
    const isWeekend = dayIndex >= 5;
    if (isWeekend) {
      return {
        table: config.weekendTableCapacity,
        bar: config.weekendBarCapacity,
        security: config.weekendSecurityCapacity,
      };
    }

    return {
      table: config.weekdayTableCapacity,
      bar: config.weekdayBarCapacity,
      security: config.weekdaySecurityCapacity,
    };
  },

  getShiftCapacityForDay(dayIndex: number, config: CalendarConfig): number {
    return dayIndex >= 5 ? config.weekendShiftCapacity : config.weekdayShiftCapacity;
  },
};
